'use client';

import { useCallback, useEffect } from "react";

type GreCaptchaEnterprise = {
    ready: (callback: () => void) => void;
    execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

const SCRIPT_ID = 'grecaptcha';

let loader: Promise<GreCaptchaEnterprise> | null = null;

const enterprise = () => (window as any).grecaptcha?.enterprise as GreCaptchaEnterprise | undefined;

const install = (siteKey: string, lang: string): Promise<GreCaptchaEnterprise> => {
    const existing = enterprise();
    if (existing?.execute) {
        return Promise.resolve(existing);
    }

    if (!loader) {
        loader = new Promise((resolve, reject) => {
            const elem = document.createElement("script");
            elem.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}&hl=${encodeURIComponent(lang)}`;
            elem.id = SCRIPT_ID;
            elem.async = true;
            elem.defer = true;
            elem.onload = () => {
                const grecaptcha = enterprise();
                grecaptcha ? grecaptcha.ready(() => resolve(grecaptcha)) : reject(new Error('grecaptcha.enterprise missing after load'));
            };
            elem.onerror = () => {
                // Allow a retry on the next submit (network hiccup, blocker toggled off...)
                loader = null;
                elem.remove();
                reject(new Error('failed to load reCAPTCHA'));
            };
            document.head.appendChild(elem);
        });
    }

    return loader;
};

/**
 * reCAPTCHA Enterprise (score-based key): loads the script once the form is shown and returns
 * getToken(action). The backend creates an assessment and checks both the score and that the
 * token was minted for the same action. Without a site key (local dev), getToken resolves to ''
 * and the backend, without RECAPTCHA_API_KEY, skips the check.
 */
export const UseGoogleReCaptcha = (siteKey: string, locale: string) => {
    const lang = locale.split('-')[0] || 'en';

    useEffect(() => {
        if (siteKey) {
            install(siteKey, lang).catch((e) => console.error('reCAPTCHA load error:', e));
        }
    }, [siteKey, lang]);

    const getToken = useCallback(async (action: string): Promise<string> => {
        if (!siteKey) {
            return '';
        }
        try {
            const grecaptcha = await install(siteKey, lang);
            return await grecaptcha.execute(siteKey, { action });
        } catch (e) {
            console.error('reCAPTCHA execute error:', e);
            return '';
        }
    }, [siteKey, lang]);

    return { getToken };
};
